import api from './api';

import {CHANNELS_SET_ACTIVE} from './channels';
import {
  analyticsAddToCart,
  analyticsBeginCheckout,
  analyticsClearEcommerce,
  analyticsProductRemoved,
} from '../middlewares/analytics/actions';
import * as Analytics from '../utils/analytics';
import ChannelPrice from '../containers/channelUtils/ChannelPrice';

export const CART_SET = 'CART_SET';
export const CART_WALLET_APPLY = 'CART_WALLET_APPLY';
export const SET_CART_PAYMENTS_METHODS = 'SET_CART_PAYMENTS_METHODS';

export default function reducer(state = null, {type, payload}) {
  switch (type) {
    case CART_SET:
      return payload;
    case CHANNELS_SET_ACTIVE:
      return null;
    case CART_WALLET_APPLY:
      return {
        ...state,
        cashback: payload.cashback,
        walletPaymentAmount: payload.walletPaymentAmount
      };
    case SET_CART_PAYMENTS_METHODS:
      return {
        ...state,
        payments: [{...state.payments[0], methodsAvailable: payload.methodsAvailable}]
      };
    default:
      return state
  }
}

export function setCart(cart) {
  return {type: CART_SET, payload: cart};
}

const extractCartItems = items => items.map(item => ({
  variant: item.variant.id,
  quantity: item.quantity,
  options: item.options.map(option => option.optionId),
  rankRangeStart: item.rankRangeStart,
  rankRangeEnd: item.rankRangeEnd,
  event: item.event && item.event.id,
  eventOccurrence: item.eventOccurrence
}));

const mapCartItems = products => products.map(product => ({
  ...product,
  options: product.options.map(optionId => ({option: optionId}))
}));

export function getCart(cartId, cartToken = undefined) {
  return dispatch => dispatch(api.get(`carts/by-id/${cartId}`, {cartToken}))
    .then(cartResponse => dispatch(setCart(cartResponse.content)))
    .catch((err) => {
      if (err.status === 404) {
        dispatch(setCart(null));
      }
    });
}

export function createCart(products) {
  return dispatch => dispatch(api.post('carts/', {items: mapCartItems(products)}))
    .then(cartResponse => {
      dispatch(setCart(cartResponse.content));

      return cartResponse;
    });
}

export function updateCart(cartId, newProducts, cartToken = undefined) {
  return dispatch => dispatch(api.post(`carts/by-id/${cartId}`, {items: mapCartItems(newProducts)}, {cartToken}))
    .then(cartResponse => {
      dispatch(setCart(cartResponse.content));

      return cartResponse;
    });
}

export function deleteCart(cartId, cartToken = undefined) {
  return dispatch => dispatch(api.delete(`carts/by-id/${cartId}`, undefined, {cartToken}))
    .then(() => dispatch(setCart(null)));
}

export function addProductToCart(product) {
  return (dispatch, getState) => {
    const currentCart = getState().cart;
    const channels = getState().channels;

    return dispatch(currentCart
      ? updateCart(currentCart.id, extractCartItems(currentCart.items).concat(product), currentCart.tokenValue)
      : createCart([product])
    )
      .then(cartResponse => {
        const addedItem = cartResponse.content.items.slice().reverse()
          .find(cartItem => cartItem.variant.id === product.variant);

        if (addedItem) {
          dispatch(analyticsClearEcommerce());
          dispatch(analyticsAddToCart(addedItem));

          const currency = (channels.activeChannel && channels.availableChannels) && channels.availableChannels[channels.activeChannel].baseCurrency;
          const currencySymbol = currency.symbol;

          const metaData = {
            code: addedItem.variant.product.code,
            name: addedItem.variant.product.name,
            'product-url': `${process.env.PUBLIC_URL}/service/${addedItem.variant.product.slug}`,
            price: `${currencySymbol}${ChannelPrice.formatPrice(currency, addedItem.total)}`
          }

          Analytics.track(Analytics.EVENTS.PRODUCT.ADD_TO_CART, metaData);
        }

        return cartResponse;
      })
      .catch((err) => {
        if (err.status === 404) {
          dispatch(setCart(null));
        }
      });
  }
}

export function removeProductFromCart(cartItemId) {
  return (dispatch, getState) => {
    const currentCart = getState().cart;
    const channels = getState().channels;
    const removedItem = currentCart.items.find(item => item.id === cartItemId);
    const newCartItems = currentCart.items.filter(item => item.id !== cartItemId);

    return dispatch(newCartItems.length
      ? updateCart(currentCart.id, extractCartItems(newCartItems), currentCart.tokenValue)
      : deleteCart(currentCart.id, currentCart.tokenValue)
    ).then(cartResponse => {
      if (removedItem) {
        dispatch(analyticsClearEcommerce());
        dispatch(analyticsProductRemoved(removedItem));

        const currency = (channels.activeChannel && channels.availableChannels) && channels.availableChannels[channels.activeChannel].baseCurrency;
        const currencySymbol = currency.symbol;

        const metaData = {
          code: removedItem.variant.product.code,
          name: removedItem.variant.product.name,
          'product-url': `${process.env.PUBLIC_URL}/service/${removedItem.variant.product.slug}`,
          price: `${currencySymbol}${ChannelPrice.formatPrice(currency, removedItem.total)}`
        }

        Analytics.track(Analytics.EVENTS.PRODUCT.REMOVE_FROM_CART, metaData);
      }

      return cartResponse;
    })
    .catch((err) => {
      if (err.status === 404) {
        dispatch(setCart(null));
      }
    });
  }
}

export function applyCouponToCart(promotionCoupon, cartId, cartToken) {
  return dispatch => dispatch(api.post(`carts/by-id/${cartId}/coupon`, {promotionCoupon}, {cartToken}))
    .then(couponResponse => dispatch(setCart(couponResponse.content)))
    .catch((err) => {
      if (err.status === 404) {
        dispatch(setCart(null));
      }

      throw err;
    });
}

export function removeCouponFromCart(cartId, cartToken) {
  return dispatch => dispatch(api.delete(`carts/by-id/${cartId}/coupon`, undefined, {cartToken}))
    .then(couponResponse => dispatch(setCart(couponResponse.content)))
    .catch((err) => {
      if (err.status === 404) {
        dispatch(setCart(null));
      }

      throw err;
    });
}

export function applyWalletPaymentToCart(cartId, walletPaymentAmount, cartToken) {
  return dispatch => dispatch(api.post(
    `carts/by-id/${cartId}/cashback/calculate`,
    {wallet_payment_amount: walletPaymentAmount},
    {cartToken}
  ))
    .then(walletResponse => {
      dispatch({
        type: CART_WALLET_APPLY,
        payload: {
          cashback: walletResponse.content.cashback,
          walletPaymentAmount: walletPaymentAmount > 0 ? walletPaymentAmount : undefined
        }
      })
      dispatch({
        type: SET_CART_PAYMENTS_METHODS,
        payload: {
          methodsAvailable: walletResponse.content.methodsAvailable,
        }
      })
    })
    .catch(() => dispatch(getCart(cartId, cartToken)));
}

export function checkoutCart(cartId, paymentMethod, checkoutFields, walletPaymentAmount, affiliateCode, cartToken, gaClientId, gaSessionId) {
  return dispatch => dispatch(api.post(
    `carts/by-id/${cartId}/checkout`,
    {
      ...(paymentMethod && {payments: [{method: paymentMethod}]}),
      ...checkoutFields,
      tos_accepted: checkoutFields.tos_accepted ? '1' : undefined,
      newsletter_subscribed: checkoutFields.newsletter_subscribed ? '1' : undefined,
      wallet_payment_amount: walletPaymentAmount,
      affiliate_code: affiliateCode,
      ...(gaClientId && { ga_client_id: gaClientId }),
      ...(gaSessionId && { ga_session_id: gaSessionId }),
    },
    {cartToken}
  )).then(checkoutResponse => {
    dispatch(analyticsClearEcommerce());
    dispatch(analyticsBeginCheckout(checkoutResponse.content));

    return checkoutResponse;
  }).catch((err) => {
    if (err.status === 404) {
      dispatch(setCart(null));
    }
  });
}

export function claimCartOwnership(cartId, cartToken) {
  return dispatch => dispatch(api.post(`carts/by-id/${cartId}/ownership`, undefined, {cartToken}))
    .catch((err) => {
      if (err.status === 404) {
        dispatch(setCart(null));
      }
    });
}

export function cleanupCartOwnership(cartId) {
  return dispatch => dispatch(api.delete(`carts/by-id/${cartId}/ownership`))
    .catch((err) => {
      if (err.status === 404) {
        dispatch(setCart(null));
      }
    });
}

export function mergeCarts(mainCart, ...mergedCarts) {
  return dispatch => dispatch(api.post(`carts/by-id/${mainCart.id}/merge`, {
    merged: mergedCarts.map(cart => ({id: cart.id, token: cart.tokenValue}))
  }, {cartToken: mainCart.tokenValue}))
    .catch((err) => {
      if (err.status === 404 || err.status === 400) {
        dispatch(setCart(null));
      }
    });
}

export function retrievePersistedCart(source) {
  return (dispatch, getState) => {
    const {channels} = getState();

    let carts;
    try {
      carts = JSON.parse(source);
    } catch (e) {
      carts = {};
    }

    if (carts[channels.activeChannel]) {
      const {id, token} = carts[channels.activeChannel];

      return dispatch(getCart(id, token)).catch(() => null);
    }

    return null;
  }
}



// WEBPACK FOOTER //
// ./ducks/cart.js